import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { AlertCircle, Check, CheckCircle2, Copy, QrCode, RefreshCw, Zap } from 'lucide-react';
import { Field, Spinner } from '@/components/app/misc';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { maskCpf, validaCPF } from '@/components/billing/masks';
/** A conferência é só uma leitura no nosso banco (o webhook do gateway grava o pagamento). */
const CHECK_INTERVAL = 5000;
/**
 * Estado do PIX da fatura. Fica na página (e não no painel) para o QR e a
 * conferência continuarem quando a pessoa alterna entre Cartão e PIX.
 *
 * - GET invoices.pay.pix?cpf_cnpj=... devolve { error, message, qr, qr_code, expires_at };
 * - contagem regressiva até expires_at; ao expirar, oferece gerar outro;
 * - a cada 5 s, GET invoices.pay.pix.check: "paid" confirma e recarrega a fatura,
 *   "pending" continua esperando e qualquer outro status pede um PIX novo.
 */
export function usePix(urls, ownerCpf) {
    const [cpf, setCpf] = React.useState(maskCpf(ownerCpf || ''));
    const [qr, setQr] = React.useState(urls.placeholder);
    const [code, setCode] = React.useState('');
    const [expiresAt, setExpiresAt] = React.useState(null);
    const [startedAt, setStartedAt] = React.useState(null);
    const [status, setStatus] = React.useState('idle');
    const [loading, setLoading] = React.useState(false);
    const [error, setError] = React.useState(null);
    const timer = React.useRef(null);
    const cpfRef = React.useRef(cpf);
    cpfRef.current = cpf;
    const clearTimer = () => {
        if (timer.current)
            window.clearTimeout(timer.current);
        timer.current = null;
    };
    React.useEffect(() => clearTimer, []);
    const reset = React.useCallback((message = null) => {
        clearTimer();
        setStatus('idle');
        setExpiresAt(null);
        setStartedAt(null);
        setQr(urls.placeholder);
        setCode('');
        setError(message);
    }, [urls.placeholder]);
    const checkPay = React.useCallback(() => {
        axios
            .get(urls.payPixCheck, { headers: { Accept: 'application/json' } })
            .then(({ data }) => {
            if (data.status === 'paid') {
                clearTimer();
                setStatus('paid');
                window.setTimeout(() => {
                    window.location.href = urls.show;
                }, 2500);
            }
            else if (data.status === 'pending') {
                timer.current = window.setTimeout(checkPay, CHECK_INTERVAL);
            }
            else {
                reset('Este PIX expirou ou foi cancelado. Gere um novo para pagar.');
            }
        })
            // Falha de rede na conferência não derruba o QR: tenta de novo mais tarde.
            .catch(() => {
            timer.current = window.setTimeout(checkPay, CHECK_INTERVAL * 2);
        });
    }, [urls.payPixCheck, urls.show, reset]);
    const getPix = React.useCallback(() => {
        clearTimer();
        setLoading(true);
        setError(null);
        axios
            .get(urls.payPix, { params: { cpf_cnpj: cpfRef.current }, headers: { Accept: 'application/json' } })
            .then(({ data }) => {
            if (data && data.error === false && (data.qr || data.qr_code)) {
                if (data.qr)
                    setQr(data.qr);
                setCode(data.qr_code || '');
                const until = data.expires_at ? new Date(data.expires_at) : null;
                setExpiresAt(until && !Number.isNaN(until.getTime()) ? until : null);
                setStartedAt(new Date());
                setStatus('waiting');
                timer.current = window.setTimeout(checkPay, CHECK_INTERVAL);
            }
            else {
                reset((data === null || data === void 0 ? void 0 : data.message) || 'Não foi possível gerar o PIX agora. Tente novamente em instantes.');
            }
        })
            .catch(() => reset('Não foi possível gerar o PIX. Verifique sua conexão e tente de novo.'))
            .finally(() => setLoading(false));
    }, [urls.payPix, checkPay, reset]);
    /** Botão "Gerar QR Code PIX" (e "Gerar novo PIX" depois de expirar). */
    const generate = () => {
        if (!validaCPF(cpfRef.current.trim().replace(/\D/g, ''))) {
            setError('Informe um CPF válido para gerar o PIX.');
            return;
        }
        getPix();
    };
    /** Contagem chegou a zero: para de conferir e mostra a opção de gerar outro. */
    const expire = React.useCallback(() => {
        clearTimer();
        setStatus((s) => (s === 'waiting' ? 'expired' : s));
    }, []);
    return { cpf, setCpf, qr, code, expiresAt, startedAt, status, loading, error, generate, expire };
}
function formatRemaining(ms) {
    const total = Math.max(0, Math.floor(ms / 1000));
    const h = Math.floor(total / 3600);
    const m = String(Math.floor(total / 60) % 60).padStart(2, '0');
    const s = String(total % 60).padStart(2, '0');
    return h > 0 ? `${h}:${m}:${s}` : `${m}:${s}`;
}
function Countdown({ from, until, onExpire }) {
    const [now, setNow] = React.useState(() => Date.now());
    React.useEffect(() => {
        const id = window.setInterval(() => setNow(Date.now()), 1000);
        return () => window.clearInterval(id);
    }, []);
    const left = until.getTime() - now;
    const ended = left <= 0;
    React.useEffect(() => {
        if (ended)
            onExpire();
    }, [ended, onExpire]);
    const total = Math.max(1, until.getTime() - from.getTime());
    const pct = Math.max(0, Math.min(100, (left / total) * 100));
    return (_jsxs("div", { className: "grid gap-1.5", children: [_jsxs("div", { className: "flex items-center justify-between text-[13px] text-muted-foreground", children: [_jsx("span", { children: "Expira em" }), _jsx("span", { className: cn('font-mono text-sm font-semibold tabular-nums text-foreground', pct < 20 && 'text-destructive'), children: formatRemaining(left) })] }), _jsx("div", { className: "h-1.5 overflow-hidden rounded-full bg-primary/15", role: "progressbar", "aria-label": "Tempo restante do QR Code", "aria-valuemin": 0, "aria-valuemax": 100, "aria-valuenow": Math.round(pct), children: _jsx("div", { className: "h-full rounded-full bg-primary transition-[width] duration-1000 ease-linear", style: { width: `${pct}%` } }) })] }));
}
async function copyToClipboard(text) {
    if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(text);
        return;
    }
    const temp = document.createElement('textarea');
    temp.value = text;
    document.body.appendChild(temp);
    temp.select();
    document.execCommand('copy');
    temp.remove();
}
function CopyButton({ text }) {
    const [copied, setCopied] = React.useState(false);
    const copy = async () => {
        if (!text) {
            toast.error('Código PIX não disponível para copiar.');
            return;
        }
        try {
            await copyToClipboard(text);
            setCopied(true);
            window.setTimeout(() => setCopied(false), 2000);
        }
        catch (e) {
            toast.error('Falha ao copiar o código PIX! Selecione o código e copie manualmente.');
        }
    };
    return (_jsxs(Button, { type: "button", variant: "outline", size: "lg", onClick: copy, className: cn('w-full font-semibold', copied
            ? 'border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-50 hover:text-emerald-700 dark:border-emerald-900 dark:bg-emerald-950 dark:text-emerald-300'
            : 'border-primary text-primary hover:bg-primary/5 hover:text-primary'), children: [copied ? _jsx(Check, {}) : _jsx(Copy, {}), copied ? 'Código copiado!' : 'Copiar código PIX'] }));
}
const STEPS = ['Abra o app do seu banco e escolha pagar com PIX.', 'Escaneie o QR Code ou cole o código copiado.', 'Confirme. Esta tela atualiza sozinha quando o pagamento cair.'];
/** PIX dentro do quadro de pagamento da fatura: CPF → QR Code → confirmação. */
export function PixPanel({ pix }) {
    const { status } = pix;
    if (status === 'idle') {
        return (_jsxs("div", { className: "grid gap-4", children: [_jsxs("div", { className: "flex gap-2.5 rounded-xl bg-muted/70 px-3.5 py-3 text-[13px]", children: [_jsx(Zap, { className: "mt-0.5 h-4 w-4 shrink-0 text-primary", "aria-hidden": "true" }), _jsx("p", { children: "Aprova\u00E7\u00E3o na hora e sem comprovante. O QR Code vale por poucos minutos." })] }), _jsx(Field, { label: "CPF do titular", htmlFor: "pix-cpf", children: _jsx(Input, { id: "pix-cpf", inputMode: "numeric", maxLength: 20, placeholder: "000.000.000-00", value: pix.cpf, onChange: (e) => pix.setCpf(maskCpf(e.target.value)), onKeyDown: (e) => {
                            if (e.key === 'Enter')
                                pix.generate();
                        }, className: "h-11" }) }), pix.error ? (_jsxs(Alert, { variant: "destructive", children: [_jsx(AlertCircle, { className: "h-4 w-4" }), _jsx(AlertDescription, { children: pix.error })] })) : null, _jsxs(Button, { type: "button", size: "lg", onClick: pix.generate, disabled: pix.loading, className: "h-12 w-full text-[15px] font-semibold", children: [pix.loading ? _jsx(Spinner, {}) : _jsx(QrCode, {}), pix.loading ? 'Gerando PIX…' : 'Gerar QR Code PIX'] })] }));
    }
    return (_jsxs("div", { className: "grid gap-4", children: [_jsx("div", { className: "flex justify-center", children: status === 'paid' ? (_jsxs("span", { className: "inline-flex items-center gap-1.5 rounded-full bg-emerald-500/10 px-3 py-1 text-xs font-semibold text-emerald-700 dark:text-emerald-300", children: [_jsx(CheckCircle2, { className: "h-3.5 w-3.5" }), "Pagamento confirmado"] })) : status === 'expired' ? (_jsx("span", { className: "inline-flex items-center rounded-full bg-muted px-3 py-1 text-xs font-semibold text-muted-foreground", children: "QR Code expirado" })) : (_jsxs("span", { role: "status", className: "inline-flex items-center gap-2 rounded-full bg-amber-500/10 px-3 py-1 text-xs font-semibold text-amber-800 dark:text-amber-300", children: [_jsxs("span", { className: "relative flex h-2 w-2", children: [_jsx("span", { className: "absolute inline-flex h-full w-full animate-ping rounded-full bg-amber-500 opacity-60 motion-reduce:hidden" }), _jsx("span", { className: "relative inline-flex h-2 w-2 rounded-full bg-amber-500" })] }), "Aguardando pagamento"] })) }), _jsxs("div", { className: "relative mx-auto w-full max-w-[220px]", children: [_jsx("img", { src: pix.qr, alt: "QR Code PIX", className: cn('aspect-square h-auto w-full rounded-xl border bg-white p-2.5', status !== 'waiting' && 'opacity-15 blur-[2px]') }), status === 'expired' ? (_jsxs("div", { className: "absolute inset-0 flex flex-col items-center justify-center gap-3 text-center", children: [_jsx("p", { className: "text-sm font-medium", children: "O tempo deste QR Code acabou." }), _jsxs(Button, { type: "button", onClick: pix.generate, disabled: pix.loading, children: [pix.loading ? _jsx(Spinner, {}) : _jsx(RefreshCw, {}), "Gerar novo PIX"] })] })) : null, status === 'paid' ? (_jsxs("div", { className: "absolute inset-0 flex flex-col items-center justify-center gap-2 text-center", children: [_jsx(CheckCircle2, { className: "h-14 w-14 text-emerald-600" }), _jsx("p", { className: "font-semibold", children: "Pagamento recebido!" }), _jsx("p", { className: "text-xs text-muted-foreground", children: "Atualizando sua fatura\u2026" })] })) : null] }), status === 'waiting' && pix.expiresAt && pix.startedAt ? _jsx(Countdown, { from: pix.startedAt, until: pix.expiresAt, onExpire: pix.expire }) : null, status === 'waiting' && pix.code ? (_jsxs("div", { className: "grid gap-2", children: [_jsx("label", { htmlFor: "pix-code", className: "text-[13px] font-medium", children: "PIX copia e cola" }), _jsx(Input, { id: "pix-code", readOnly: true, value: pix.code, className: "h-11 bg-muted/50 font-mono text-xs", onFocus: (e) => e.target.select() }), _jsx(CopyButton, { text: pix.code })] })) : null, status === 'waiting' ? (_jsx("ol", { className: "grid gap-2 rounded-xl border p-3.5 text-[13px] text-muted-foreground", children: STEPS.map((step, i) => (_jsxs("li", { className: "flex items-start gap-2.5", children: [_jsx("span", { className: "flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary/10 text-[11px] font-bold text-primary", children: i + 1 }), _jsx("span", { children: step })] }, step))) })) : null] }));
}
